
import { FC, useState, useEffect } from 'react';
import { makeStyles, shorthands, tokens, Input, Avatar, Tag, TagGroup, Divider, Spinner } from '@fluentui/react-components';
import tokensOverride from '../../design/tokensOverride';
import { Building, Search as SearchIcon, TriangleAlert, Users } from 'lucide-react';
import SyButton from '../Common/SyButton';
import SpinnerOverlay from '../Common/SpinnerOverlay';
import { Dismiss24Regular } from '@fluentui/react-icons';
// import { CSSProperties } from 'react';
import { CDialog } from '../Dialogs/CDialog';
import { ServiceFactory } from '../../EnterpriseGPTApi/ServiceFactory';
import { UserData, PersonalGptResponse } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useUserPreferences, useFeature } from '../../store/hooks';

interface Person {
    name: string;
    email: string;
    avatarUrl?: string;
    initials?: string;
}

interface ShareGPTProps {
    open: boolean;
    gptId: string;
    useOwnerApi?: boolean;
    onClose: () => void;
    onShare: (gptId: string, emails: string[], message: string) => void;
    onRemove: (gptId: string, email: string) => Promise<void>;
    onSetPublicStatus: (gptId: string, isPublic: boolean) => Promise<void>;
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('16px'),
        minWidth: 'calc(320px * 1.1)',
        maxWidth: 'calc((100vw - 48px) * 1.1)',
        width: '100%',
        minHeight: '430px',
        maxHeight: 'calc(100vh - 120px)',
        overflow: 'hidden',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '0',
        backgroundColor: '#FFFFFF',
    },
    rootDark: {
        backgroundColor: '#18181B',
    },
    header: {
        padding: '25px 26px 0px 26px',
        position: 'relative',
    },
    title: {
        fontWeight: 600,
        fontSize: '18px',
        color: '#000000',
    },
    titleDark: {
        color: '#ffffff',
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        cursor: 'pointer',
        ...shorthands.padding(0),
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.borderRadius('8px'),
        width: '38px',
        height: '38px',
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '&:active': {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
        },
    },
    closeIcon: {
        width: '16px',
        height: '16px',
        color: tokens.colorNeutralForeground1,
    },
    info: {
        background: 'none',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        marginBottom: tokens.spacingVerticalXS,
        marginTop: tokens.spacingVerticalS,
    },
    infoWithTop8: {
        marginTop: '21px',
        background: '#FFEBE6 !important',
        padding: '12px 12px',
        borderRadius: tokens.borderRadiusLarge,
    },
    infoWithTop8Dark: {
        background: 'var(--color-zinc-800) !important',
        border: `1px solid var(--color-zinc-700) !important`,
    },
    infoWithTopRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
    },
    alertIcon: {
        color: 'var(--color-primary-500)',
        padding: '4px 4px 0 4px',
        display: 'flex',
        alignItems: 'flex-start',
    },
    inputRow: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        rowGap: '8px',
        columnGap: '8px',
        paddingTop: '0px',
        paddingRight: '26px',
        paddingBottom: '12px',
        paddingLeft: '26px',
        minWidth: 0,
    },
    inputFlex: {
        flex: 1,
    },
    searchIconWrapper: {
        position: 'absolute',
        left: '36px',
        top: '23px',
        transform: 'translateY(-50%)',
        color: tokens.colorNeutralForeground3,
        background: 'transparent',
        zIndex: 2,
    },
    searchInput: {
        flex: 1,
        minWidth: '240px',
        fontSize: tokens.fontSizeBase400,
        paddingLeft: '36px',
        background: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        paddingTop: '10px',
        paddingBottom: '10px',
    },
    searchIcon: {
        position: 'absolute',
        left: '36px', 
        top: '23px',
        transform: 'translateY(-50%)',
        color: tokens.colorNeutralForeground3,
        background: 'transparent',
        zIndex: 2,
    },
    shareButton: {
        flex: '0 0 auto',
        marginLeft: '12px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
    },
    syLikeInput: {
        width: '100%',
        borderRadius: '0.5rem',
        background: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        boxShadow: 'none !important',
        padding: '0',
        '&:focus-within': {
            outline: '2px solid var(--color-primary-400) !important',
            outlineOffset: '2px !important',
            border: `1px solid ${tokens.colorNeutralStroke2} !important`,
            boxShadow: 'none !important',
            background: `${tokens.colorNeutralBackground1} !important`,
        },
        '& .react-select__control': {
            border: 'none',
            boxShadow: 'none',
            paddingLeft: '24px',
            background: 'transparent',
            borderRadius: 'inherit',
        },
        '& .react-select__control--is-focused': {
            boxShadow: 'none',
            outline: 'none',
        },
        '& .react-select__input-container': {
            paddingLeft: '0px',
            color: tokens.colorNeutralForeground1,
        },
        '& .react-select__input': {
            paddingLeft: '32px',
            color: tokens.colorNeutralForeground1,
        },
        '& .react-select__placeholder': {
            color: tokens.colorNeutralForeground3,
        },
        '& .react-select__single-value': {
            color: tokens.colorNeutralForeground1,
        },
        '& .react-select__menu': {
            borderRadius: tokens.borderRadiusLarge,
            overflow: 'hidden',
            backgroundColor: tokens.colorNeutralBackground1,
        },
        '& .react-select__option': {
            backgroundColor: tokens.colorNeutralBackground1,
            color: tokens.colorNeutralForeground1,
        },
        '& .react-select__menu-notice': {
            color: tokens.colorNeutralForeground3,
        },
        '& .react-select__multi-value': {
            backgroundColor: tokens.colorNeutralBackground4,
        },
        '& .react-select__multi-value__label': {
            color: tokens.colorNeutralForeground1,
        },
        '& .react-select__multi-value__remove': {
            backgroundColor: tokens.colorNeutralBackground5,
            color: tokens.colorNeutralForeground3,
        },
    },
    tagGroup: {
        margin: '0 26px',
        minWidth: 0,
        overflowX: 'hidden',
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('8px'),
    },
    popoverList: {
        background: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusLarge,
        boxShadow: tokens.shadow8,
        padding: '8px 0',
        minWidth: '360px',
        zIndex: 1002,
    },
    popoverItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 22px',
        cursor: 'pointer',
        ...shorthands.gap('12px'),
        '&:hover': {
            background: tokens.colorNeutralBackground1Hover,
        },
    },
    personList: {
        margin: '14px 26px 0 26px',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('8px'),
        maxHeight: '50vh',
        overflowY: 'auto',
        paddingRight: '6px',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        minWidth: 0,
    },
    personListTitle: {
        fontSize: '15px',
        fontWeight: 520,
        marginBottom: '4px',
        color: '#000000',
    },
    personListTitleDark: {
        color: '#ffffff',
    },
    importantLabel: {
        fontWeight: 600,
        color: '#000000',
        marginRight: '6px',
    },
    importantLabelDark: {
        color: '#FFFFFF',
    },
    infoWithNegativeTop: {
        marginTop: '-8px',
    },
    personRow: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('12px'),
        padding: '12px 12px',
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: '#F9FAFB',
        },
    },
    personRowDark: {
        '&:hover': {
            backgroundColor: '#1f1f23 !important',
        },
    },
    personDivider: {
        margin: '0 1px',
        height: '1px',
        backgroundColor: tokens.colorNeutralStroke1,
        opacity: 0.2,
    },
    personInfo: {
        display: 'flex',
        flexDirection: 'column',
    },
    personName: {
        fontWeight: 500,
        fontSize: tokens.fontSizeBase400,
    },
    personEmail: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
    },
    removeBtn: {
        marginLeft: 'auto',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: tokens.colorNeutralForeground3,
        padding: '4px',
        borderRadius: tokens.borderRadiusMedium,
        '&:hover': {
            background: tokens.colorNeutralBackground1Hover,
            color: tokens.colorPaletteRedForeground1,
        },
    },
    removeIcon: {
        width: '18px',
        height: '18px',
        color: tokens.colorNeutralForeground3,
    },
    dividerWrapper: {
        marginLeft: '26px',
        marginRight: '26px',
        marginTop: '-6px',
    },
    footer: {
        padding: '12px 26px 20px 26px',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
    },
    orgShareToggleContainer: {
        backgroundColor: '#F9FAFB',
        padding: '12px 16px',
        margin: '0 26px',
        borderRadius: '8px',
        overflow: 'visible',
    },
    orgShareToggleContainerDark: {
        backgroundColor: 'var(--color-zinc-800)',
        border: `1px solid var(--color-zinc-700)`,
    },
    orgShareToggleContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
    },
    orgShareInfo: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        flex: 1,
    },
    orgShareIcon: {
        fontSize: '20px',
        color: 'var(--color-primary-500)',
        marginTop: '2px',
        flexShrink: 0,
    },
    orgShareIconDark: {
        color: 'var(--color-primary-400)',
    },
    orgShareText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    orgShareLabel: {
        fontSize: '16px',
        fontWeight: 500,
        color: 'var(--color-text-primary)',
        lineHeight: 'normal',
    },
    orgShareLabelDark: {
        color: '#fafafa',
    },
    orgShareSubtitle: {
        fontSize: '14px',
        color: 'var(--color-gray-500) !important',
        display: 'block',
        marginTop: '-4px',
    },
    orgShareSubtitleDark: {
        color: '#a1a1a1',
    },
    btnMakePublic: {
        flexShrink: 0,
    },
});

const useDarkMode = () => {
    const [isDarkMode, setIsDarkMode] = useState(document.body.getAttribute('data-theme') === 'dark');

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDarkMode(document.body.getAttribute('data-theme') === 'dark');
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);

    return isDarkMode;
};

export const ShareGPT: FC<ShareGPTProps> = ({ open, gptId, useOwnerApi = false, onClose, onShare, onRemove, onSetPublicStatus }) => {
    const styles = useStyles();
    const isDarkMode = useDarkMode();
    const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
    const [search, setSearch] = useState('');
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [searchResults, setSearchResults] = useState<UserData[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [sharedPeople, setSharedPeople] = useState<Person[]>([]);
    const [loading, setLoading] = useState(true);
    const [isRemoving, setIsRemoving] = useState(false);
    const [isOrgShared, setIsOrgShared] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const chatFeature = useFeature('chat');

    const maxShares = chatFeature.personalGpt?.maxNumberOfSharesPerGpt ?? null;
    console.log('[ShareGPT] maxNumberOfSharesPerGpt:', maxShares, '| sharedPeople:', sharedPeople.length);
    const isShareLimitReached = maxShares !== null && sharedPeople.length >= maxShares;
    const wouldExceedLimit = (added: number) => maxShares !== null && sharedPeople.length + added > maxShares;

    // Fetch GPT info and shared people when component opens
    useEffect(() => {
        if (open && gptId) {
            const controller = new AbortController();

            const fetchGptData = async () => {
                try {
                    setLoading(true);
                    const apiClient = ServiceFactory.EnterpriseGPTApiClient();
                    
                    // Get Personal GPT info
                    const gptData = useOwnerApi
                        ? await apiClient.getAnyGpt(gptId, controller.signal)
                        : await apiClient.getPersonalGpt(gptId, controller.signal);
                    
                    // Set isOrgShared state based on server data
                    setIsOrgShared(gptData.isPublic || false);
                    
                    // Get users from emails if there are shared emails
                    if (gptData.sharedWith && gptData.sharedWith.length > 0) {
                        const users = await apiClient.usersFromEmails(gptData.sharedWith, controller.signal);
                        
                        // Convert UserData to Person format
                        const people: Person[] = users.map(user => ({
                            name: user.fullName || '',
                            email: user.email || '',
                            avatarUrl: user.avatar,
                            initials: user.initials
                        }));
                        
                        setSharedPeople(people);
                    } else {
                        setSharedPeople([]);
                    }
                } catch (error) {
                    if (!controller.signal.aborted) {
                        console.error('Error fetching GPT data:', error);
                        setSharedPeople([]);
                    }
                } finally {
                    if (!controller.signal.aborted) {
                        setLoading(false);
                    }
                }
            };
            
            fetchGptData();
            return () => controller.abort();
        }
    }, [open, gptId, useOwnerApi]);

    useEffect(() => {
        if (!search || search.length < 2) {
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const apiClient = ServiceFactory.EnterpriseGPTApiClient();
                const users = await apiClient.searchUsers(search);
                setSearchResults(
                    users
                        .filter(u => !selectedPeople.some(sp => sp.email === u.email))
                        .slice(0, 5)
                );
            } catch {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [search, selectedPeople]);

    const handleAddPerson = (person: Person) => {
        setSelectedPeople([...selectedPeople, person]);
        setSearch('');
        setPopoverOpen(false);
    };

    const handleRemovePerson = (email: string) => {
        setSelectedPeople(selectedPeople.filter(p => p.email !== email));
    };

    const handleRemoveShared = async (email: string) => {
        setIsRemoving(true);
        try {
            // Update UI immediately
            setSharedPeople(sharedPeople.filter(p => p.email !== email));
            // Call the server to update the shared list
            await onRemove(gptId, email);
        } finally {
            setIsRemoving(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
           setSearch(e.target.value);
           setPopoverOpen(e.target.value.length >= 2);
    };

    const handleShare = () => {
        if (wouldExceedLimit(selectedPeople.length)) return;
        const emailsToShare = selectedPeople.map(p => p.email);
        onShare(gptId, emailsToShare, '');
        
        // Update the shared people list with the newly shared people
        setSharedPeople([...sharedPeople, ...selectedPeople]);
        
        // Clear the selected people after sharing
        setSelectedPeople([]);
    };

    const handleSetPublicStatus = async (isPublic: boolean) => {
        try {
            await onSetPublicStatus(gptId, isPublic);
            setIsOrgShared(isPublic);
            if (isPublic) {
                setShowConfirmation(false);
            }
        } catch (error) {
            console.error('Error updating GPT public status:', error);
        }
    };



    return (
        <CDialog
            open={open}
            primaryActionText=" "
            onPrimaryAction={() => {}}
            dialogSurfaceStyle={{ padding: 0, width: '692px', maxWidth: 'calc(100vw - 48px)' }}
        >
            <div className={`${styles.root} ${isDarkMode ? styles.rootDark : ''}`}>
                <div className={styles.header}>
                    <div className={`${styles.title} ${isDarkMode ? styles.titleDark : ''}`}>Share this GPT</div>
                    <button aria-label="Close" onClick={onClose} className={styles.closeButton}>
                        <Dismiss24Regular className={styles.closeIcon} />
                    </button>

                    <div className={`${styles.info} ${styles.infoWithTop8} ${isDarkMode ? styles.infoWithTop8Dark : ''} ${styles.infoWithTopRow}`}>
                        <div className={styles.alertIcon}>
                            <FontAwesomeIcon 
                                icon={faExclamationTriangle} 
                                size="lg"
                            />
                        </div>

                        <div style={{ paddingTop: 2 }}>
                            <span className={`${styles.importantLabel} ${isDarkMode ? styles.importantLabelDark : ''}`}>Important:</span>
                            As a GPT editor, you are accountable for sharing GPTs responsibly. Ensure access is limited to authorized individuals and aligned with the organization’s data-sharing policies and need-to-know principles.
                        </div>
                    </div>

                </div>


                {isShareLimitReached && (
                    <div className={`${styles.info} ${styles.infoWithTop8} ${isDarkMode ? styles.infoWithTop8Dark : ''} ${styles.infoWithTopRow}`}
                        style={{ margin: '0 26px' }}>
                        <div className={styles.alertIcon}>
                            <TriangleAlert size={18} />
                        </div>
                        <div style={{ paddingTop: 2 }}>
                            <span className={`${styles.importantLabel} ${isDarkMode ? styles.importantLabelDark : ''}`}>Share limit reached:</span>
                            This GPT has been shared with the maximum allowed number of users ({maxShares}). Remove existing users before adding new ones.
                        </div>
                    </div>
                )}

                <div style={{ position: 'relative' }}>
                <div className={styles.inputRow}>

                    <span className={styles.searchIconWrapper}>
                        <SearchIcon size={18} />
                    </span>

                    <div className={styles.inputFlex}>
                        <Input
                            style={{ width: '100%', paddingLeft: '32px' }}
                            value={search}
                            disabled={isShareLimitReached}
                            onChange={handleInputChange}
                            placeholder="Search users to share with..."
                        />
                    </div>

                    <SyButton
                        appearance='primary'
                        className={styles.shareButton}
                        onClick={handleShare}
                        disabled={selectedPeople.length === 0 || isShareLimitReached || wouldExceedLimit(selectedPeople.length)}
                    >
                        Share
                    </SyButton>

                </div>

                {popoverOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 26,
                        right: 26,
                        zIndex: 10,
                        maxHeight: 200,
                        overflowY: 'auto',
                        backgroundColor: tokens.colorNeutralBackground1,
                        boxShadow: tokens.shadow8,
                        borderRadius: tokens.borderRadiusMedium,
                    }}>
                        {isSearching && <Spinner size='tiny' label='Loading...' />}
                        {!isSearching && searchResults.length === 0 && (
                            <div style={{ padding: '8px 4px', color: tokens.colorNeutralForeground3, fontSize: '14px' }}>No matches found</div>
                        )}
                        {!isSearching && searchResults.map(user => (
                            <div
                                key={user.email}
                                onClick={() => handleAddPerson({ name: user.fullName || '', email: user.email || '', avatarUrl: user.avatar, initials: user.initials })}
                                style={{
                                    padding: '8px 4px',
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                                }}
                            >
                                <div>{user.fullName || user.email}</div>
                                {user.fullName && user.fullName !== user.email && (
                                    <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{user.email}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                </div>

                {selectedPeople.length > 0 && (
                    <TagGroup
                        className={styles.tagGroup}
                        onDismiss={(_e, { value }) => handleRemovePerson(value as string)}
                    >
                        {selectedPeople.map(p => (
                            <Tag key={p.email} value={p.email} dismissible>
                                {p.name || p.email}
                            </Tag>
                        ))}
                    </TagGroup>
                )}

                {/* Organization Sharing Toggle Section */}
                { chatFeature.personalGpt!.hasRightToShareGptAsPublic && (
                <div className={`${styles.orgShareToggleContainer} ${isDarkMode ? styles.orgShareToggleContainerDark : ''}`}>
                    <div className={styles.orgShareToggleContent}>
                        <div className={styles.orgShareInfo}>
                            <i className={`${styles.orgShareIcon} ${isDarkMode ? styles.orgShareIconDark : ''}`}>
                                <Building />
                            </i>
                            <div className={styles.orgShareText}>
                                <span className={`${styles.orgShareLabel} ${isDarkMode ? styles.orgShareLabelDark : ''}`}>Share this GPT with the whole organization</span>
                                <span className={`${styles.orgShareSubtitle} ${isDarkMode ? styles.orgShareSubtitleDark : ''}`}>All members will have viewer access</span>
                            </div>
                        </div>

                        {!isOrgShared && (
                            <SyButton
                                appearance="outline"
                                className={styles.btnMakePublic}
                                onClick={() => handleSetPublicStatus(true)}                            
                                icon={<Users />}
                            >
                                Make Public
                            </SyButton>
                        )}

                        {isOrgShared && (
                            <SyButton
                                appearance="tertiary"
                                className={styles.btnMakePublic}
                                onClick={() => handleSetPublicStatus(false)}                            
                                icon={<Users />}
                            >
                                Back to Private
                            </SyButton>
                        )}

                    </div>
                </div>)}
                            
                <div className={styles.personList} style={{ opacity: isOrgShared ? 0.5 : 1 }}>
                    <div className={`${styles.personListTitle} ${isDarkMode ? styles.personListTitleDark : ''}`}>People with viewer access</div>

                    <div className={`${styles.info} ${styles.infoWithNegativeTop}`}>
                        <span>
                            When a GPT is shared in view-only mode, users with access can use the GPT but cannot edit it. Only the owner can make changes.
                        </span>
                    </div>

                    <SpinnerOverlay isLoading={isRemoving}>
                        {sharedPeople.map((person, idx) => (
                            <div key={person.email + idx}>
                                <div className={`${styles.personRow} ${isDarkMode ? styles.personRowDark : ''}`}>
                                    <Avatar name={person.name} image={{ src: person.avatarUrl || undefined }} size={28} />
                                    <div className={styles.personInfo}>
                                        <span className={styles.personName}>{person.name}</span>
                                        <span className={styles.personEmail}>{person.email}</span>
                                    </div>
                                    <button className={styles.removeBtn} title="Remove" onClick={() => handleRemoveShared(person.email)} disabled={isOrgShared}>
                                        <Dismiss24Regular className={styles.removeIcon} />
                                    </button>
                                </div>
                                {idx < sharedPeople.length - 1 && <div className={styles.personDivider} />}
                            </div>
                        ))}
                    </SpinnerOverlay>
                </div>
                {/* Removed extra footer space */}
            </div>
        </CDialog>
    );
};

export default ShareGPT;
